import {SessionPeriod} from "../../Server/DB/@Shared/SessionPeriod.js";
import {GetSessionPeriods} from "../../Server/DB/@Shared/SessionPeriodUtils.js";
import {GetJournalEntries, SortJournalEntries} from "../../Server/DB/journalEntries.js";
import {CombineEntryAndSegment} from "../../Server/DB/journalEntries/$journalEntry.js";
import {JournalEntry, JournalSegment} from "../../Server/DB/journalEntries/@JournalEntry.js";
import {GetSessions} from "../../Server/DB/sessions.js";
import {MeID} from "../../Server/DB/users.js";
import {GetEntries} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Column, Row, Select, Text} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {dayInMS, hourInMS, minuteInMS, secondInMS} from "web-vcore";
import {GetAnchorStatDayForTargetTime, GetStatNightContainingX} from "./Helpers.js";
import {Phase} from "../../Server/DB/@Shared/Phase.js";
import {EstimateSegmentEndTime, EstimateSegmentStartTime} from "../../Server/DB/@Shared/JournalTimeEstimates.js";

enum SpecialStatTypes {
	"none",
	"percentOfLucidsInFirstFourHours",
	"givenAudioX_averageDreamDuration",
	"longestLucidStreaks",
}

export function ShowSpecialStatsDialog() {
	ShowMessageBox({
		title: "Special stats",
		message: ()=><SpecialStatsUI/>,
	});
}

export type SpecialStatsUI_CoreData = ReturnType<typeof SpecialStatsUI_GetCoreData>;
export function SpecialStatsUI_GetCoreData() {
	const journalEntries_raw = GetJournalEntries(MeID());
	const journalEntries_sorted = SortJournalEntries(journalEntries_raw, true);
	const sessions = GetSessions(MeID());
	const sessionPeriods = GetSessionPeriods(journalEntries_sorted, sessions, true);
	return {journalEntries_raw, journalEntries_sorted, sessions, sessionPeriods};
}

const SpecialStatsUI = observer_mgl(()=>{
	const data = SpecialStatsUI_GetCoreData();
	const [statType, setStatType] = React.useState(SpecialStatTypes.percentOfLucidsInFirstFourHours);
	return (
		<>
			<Row>
				<Select displayType="button bar" options={GetEntries(SpecialStatTypes, "ui")} value={statType} onChange={val=>setStatType(val)}/>
			</Row>
			{statType == SpecialStatTypes.percentOfLucidsInFirstFourHours && <StatUI_percentOfLucidsInFirstFourHours data={data}/>}
			{statType == SpecialStatTypes.givenAudioX_averageDreamDuration && <StatUI_givenAudioX_averageDreamDuration data={data}/>}
			{statType == SpecialStatTypes.longestLucidStreaks && <StatUI_longestLucidStreaks data={data}/>}
		</>
	);
});

const StatUI_percentOfLucidsInFirstFourHours = observer_mgl(({data}: {data: SpecialStatsUI_CoreData})=>{
	const info = GetInfoOnLucidsPreOrPostFirstFourHours(data.sessionPeriods);
	const infoStr = [
		info.percentOfLucidsInFirstFourHours.ToPercentStr(1),
		`(${info.lucidsPre4hrMark.length} <4hr; ${info.lucidsPost4hrMark.length} >=4hr)`,
	].join(" ");
	return (
		<Row mt={5} sel>
			<Text>Lucid % in first 4hr of sleep:</Text>
			<Text ml={5}>{infoStr}</Text>
		</Row>
	);
});

const StatUI_givenAudioX_averageDreamDuration = observer_mgl(({data}: {data: SpecialStatsUI_CoreData})=>{
	const dreamDurationsForAudioX = new Map<string, number[]>(); // in minutes
	for (const session of data.sessions) {
		for (const [i, event] of session.events.entries()) {
			const eventsBefore = session.events.slice(0, i);
			const eventsAfter = session.events.slice(i + 1);
			if (event.type == "EffectPointer.Play") {
				const phaseSetBefore = eventsBefore.findLast(a=>a.type == "Alarms.PhaseSet");
				const phaseSetAfter = eventsAfter.find(a=>a.type == "Alarms.PhaseSet");
				if (phaseSetBefore?.phase == Phase.dreaming && phaseSetAfter && phaseSetAfter.phase != Phase.dreaming) {
					const dreamStartTime = phaseSetBefore.date;
					const dreamEndTime = phaseSetAfter.date;
					const timeSinceDreamStart = event.date - dreamStartTime;
					// if this is the audio played as the "backgroud sound during dreams", it should start within 5s of dream detection
					if (timeSinceDreamStart <= 5 * secondInMS) {
						const audioPath = event.effectPointer_play?.soundFile_filePath ?? "n/a";
						dreamDurationsForAudioX.set(audioPath, [
							...(dreamDurationsForAudioX.get(audioPath) ?? []),
							(dreamEndTime - dreamStartTime) / minuteInMS
						]);
					}
				}
			}
		}
	}

	// if there is a "shared prefix" to the audio-paths, find it
	let sharedPrefix = "";
	const audioPaths = [...dreamDurationsForAudioX.keys()];
	if (audioPaths.length > 1) {
		const firstPath = audioPaths[0];
		for (let i = 0; i < firstPath.length; i++) {
			const prefix = firstPath.slice(0, i + 1);
			if (audioPaths.every(a=>a.startsWith(prefix))) {
				sharedPrefix = prefix;
			} else {
				break;
			}
		}
		// keep the shared prefix only up to the last "/" to avoid cutting mid-filename
		const lastSlashIndex = sharedPrefix.lastIndexOf("/");
		if (lastSlashIndex != -1) {
			sharedPrefix = sharedPrefix.slice(0, lastSlashIndex + 1);
		}
	}

	return (
		<Column sel>
			{[...dreamDurationsForAudioX.entries()].map(([audioPath, durations])=>{
				const averageDuration = durations.Average();
				const displayPath = sharedPrefix ? audioPath.slice(sharedPrefix.length) : audioPath;
				const sorted = [...durations].sort((a, b)=>a - b);
				const percentile = (p: number)=>{
					const idx = (p / 100) * (sorted.length - 1);
					const lower = Math.floor(idx);
					const upper = Math.ceil(idx);
					return (sorted[lower] + sorted[upper]) / 2;
				};
				return (
					<>
						<Row mt={5} key={audioPath}>
							<Text>{displayPath}:</Text>
						</Row>
						<Row mt={5} ml={10}>
							<Text>{[
								`Count: ${durations.length}`,
								`Avg: ${averageDuration.toFixed(1)}m`,
								`P10: ${percentile(10).toFixed(1)}m`,
								`P30: ${percentile(30).toFixed(1)}m`,
								`P50: ${percentile(50).toFixed(1)}m`,
								`P70: ${percentile(70).toFixed(1)}m`,
								`P90: ${percentile(90).toFixed(1)}m`,
							].join(" | ")}</Text>
						</Row>
					</>
				);
			})}
		</Column>
	);
});

function GetInfoOnLucidsPreOrPostFirstFourHours(sessionPeriods: SessionPeriod[]) {
	const dreamSegmentsPre4hrMark = [] as JournalSegment[];
	const dreamSegmentsPost4hrMark = [] as JournalSegment[];
	for (const period of sessionPeriods) {
		const dreamSegments = period.journalSegments.filter(a=>a.wakeTime == null);
		const wakeSegments = period.journalSegments.filter(a=>a.wakeTime != null);
		if (dreamSegments.length == 0 || wakeSegments.length == 0) continue;
		// also exclude nights where there are fewer than two dream+wake pairs, since that likely indicates incomplete data
		// (eg. session malfunction -> no wake-segments auto-added -> only 1 wake-segment -> "sleep start" supposedly 90m before final wake)
		if (dreamSegments.length < 2 || wakeSegments.length < 2) continue;

		const fallAsleepTime = period.sleepStartTime;
		if (fallAsleepTime == null) continue;
		const fourHourMark = fallAsleepTime + (4 * hourInMS);
		// toggleable (alt sleep-start calculation): define sleep-start as being 90m before first wake-segment's wake-time
		/*const firstWakeTime = wakeSegments[0].wakeTime!;
		const fallAsleepTime = firstWakeTime - (90 * minuteInMS);
		const fourHourMark = fallAsleepTime + (4 * hourInMS);*/

		// toggleable (alt night-exclusion criteria): also exclude nights of <4hr total duration
		/*const totalDuration = EstimateSegmentEndTime(period.journalEntries.find(a=>a.segments.includes(dreamSegments.Last()))!, dreamSegments.Last())! - fallAsleepTime;
		if (totalDuration < 4 * hourInMS) continue;*/

		for (const dream of dreamSegments) {
			const dreamWakeTime = EstimateSegmentEndTime(period.journalEntries.find(a=>a.segments.includes(dream))!, dream);
			if (dreamWakeTime == null) continue;
			if (dreamWakeTime < fourHourMark) {
				dreamSegmentsPre4hrMark.push(dream);
			} else {
				dreamSegmentsPost4hrMark.push(dream);
			}
		}
	}

	const lucidsPre4hrMark = dreamSegmentsPre4hrMark.filter(a=>a.lucid);
	const lucidsPost4hrMark = dreamSegmentsPost4hrMark.filter(a=>a.lucid);
	const percentOfLucidsInFirstFourHours = (lucidsPre4hrMark.length + lucidsPost4hrMark.length) > 0
		? lucidsPre4hrMark.length / (lucidsPre4hrMark.length + lucidsPost4hrMark.length)
		: 0;
	return {lucidsPre4hrMark, lucidsPost4hrMark, percentOfLucidsInFirstFourHours};
}

const StatUI_longestLucidStreaks = observer_mgl(({data}: {data: SpecialStatsUI_CoreData})=>{
	const dreams = data.journalEntries_sorted.flatMap(a=>a.segments.map(b=>CombineEntryAndSegment(a, b)))
		.filter(dream=>{
			if (dream.segment.wakeTime != null) return false;
			if (!dream.segment.lucid) return false;
			return true;
		}).OrderBy(dream=>{
			return EstimateSegmentStartTime(dream.entry, dream.segment);
		});

	type StreakInfo = {nightCount: number, lucidCount: number, lastLucid_statNight: number};
	let longestStreak_info: StreakInfo|n;
	let currentStreak_info = {nightCount: 0, lucidCount: 0, lastLucid_statNight: 0};
	for (const dream of dreams) {
		const dreamStartTime = EstimateSegmentStartTime(dream.entry, dream.segment);
		if (dreamStartTime == null) continue;
		const dream_statNight = GetStatNightContainingX(dreamStartTime);
		const daysSinceLastLucid = ((dream_statNight - currentStreak_info.lastLucid_statNight) / dayInMS).RoundTo(1);

		// if it's been >1 day since current streak's last lucid, we're on a new sequence/streak, so reset size counter
		if (daysSinceLastLucid > 1) {
			currentStreak_info = {nightCount: 1, lucidCount: 1, lastLucid_statNight: dream_statNight};
		}
		// else, we're within the same streak...
		else {
			// so IF this lucid is in a new (ie. the next) night, increment the streak-size counter
			if (daysSinceLastLucid == 1) {
				currentStreak_info.nightCount++;
				currentStreak_info.lucidCount++;
				currentStreak_info.lastLucid_statNight = dream_statNight;
			} else {
				// lucid in same night, just increment lucidCount
				currentStreak_info.lucidCount++;
			}
		}

		// if current-streak exceeds prior longest-streak, update longest-streak with current one's info
		if (!longestStreak_info || currentStreak_info.nightCount > longestStreak_info.nightCount) {
			longestStreak_info = {...currentStreak_info};
		}
	}
	return (
		<>
			<Row mt={5} sel>Longest lucid streak...</Row>
			{longestStreak_info && <>
				<Row mt={5} sel>* Night count (ie. streak size): {longestStreak_info!.nightCount} nights (with at least 1 lucid)</Row>
				<Row mt={5} sel>* Lucid count: {longestStreak_info!.lucidCount}</Row>
				<Row mt={5} sel>* End date: {new Date(longestStreak_info!.lastLucid_statNight).toLocaleDateString()}</Row>
			</>}
			{!longestStreak_info && <Row mt={5}>(no lucid-streaks found)</Row>}
		</>
	);
});